import { useMemo } from 'react'

import { MatchingPayoutRequisite, REQUISITE_KIND_LABELS } from '../../core/_models'
import { SmartTooltip } from '../../../../../components/SmartTooltip/SmartTooltip'
import styles from './PayoutRequisites.module.css'

/**
 * То, что нужно для отрисовки одного канала. Отдельный тип от MatchingPayoutRequisite: у канала
 * из fallback нет токена — выплата без строк связки не имеет адресуемого канала и её реквизит
 * менять нечем, — а рисуется он ровно так же.
 */
type DisplayChannel = Pick<MatchingPayoutRequisite, 'kind' | 'identifier' | 'bank' | 'cardholder'>

interface Props {
  /** Каналы выплаты в порядке привязки (CPAYMENT-1713). */
  requisites?: MatchingPayoutRequisite[] | null
  /** Первичный канал из скалярных полей ответа — витрина для выплат без связки. */
  fallback: {
    identifier?: string | null
    bank?: string | null
    cardholder?: string | null
  }
}

/**
 * Блок реквизитов выплаты в списках мэтчинга.
 *
 * Выплата может нести несколько каналов (карта + телефон СБП), но строка таблицы столько не
 * вмещает: в потоке остаётся первый, остальные раскрываются по наведению — та же механика, что у
 * параметров профилей мэтчинга. Каналы разделены бледной чертой, чтобы «Банк» второго не читался
 * как продолжение первого.
 *
 * Один канал (подавляющее большинство выплат) рисуется ровно как раньше — ни подсказки, ни
 * дополнительного элемента в строке не появляется.
 */
export const PayoutRequisites = ({ requisites, fallback }: Props) => {
  const channels = useMemo<DisplayChannel[]>(() => {
    if (requisites?.length) return requisites

    // Выплата без строк связки: показываем то же, что и до CPAYMENT-1713.
    if (!fallback.identifier && !fallback.bank && !fallback.cardholder) return []

    return [
      {
        identifier: fallback.identifier ?? '',
        bank: fallback.bank ?? null,
        cardholder: fallback.cardholder ?? null,
      },
    ]
  }, [requisites, fallback.identifier, fallback.bank, fallback.cardholder])

  if (channels.length === 0) {
    return <span className='text-muted'>-</span>
  }

  const [first, ...rest] = channels

  if (rest.length === 0) {
    return <Channel channel={first} />
  }

  return (
    <SmartTooltip
      bare
      content={
        <div className={styles.panel}>
          {channels.map((channel, index) => (
            <div key={index} className={index > 0 ? styles.divided : undefined}>
              <Channel channel={channel} />
            </div>
          ))}
        </div>
      }
    >
      <div className={styles.anchor}>
        <Channel channel={first} />
        <div className={styles.more}>ещё {rest.length}</div>
      </div>
    </SmartTooltip>
  )
}

const Channel = ({ channel }: { channel: DisplayChannel }) => {
  const kindLabel = channel.kind ? REQUISITE_KIND_LABELS[channel.kind] : undefined

  return (
    <div className='d-flex flex-column gap-1'>
      {kindLabel && <span className='text-muted fs-7'>{kindLabel}</span>}
      <div>
        <span className='text-muted fs-7'>Реквизит:</span>{' '}
        <span className='fw-bold'>{channel.identifier || '-'}</span>
      </div>
      {channel.bank && (
        <div>
          <span className='text-muted fs-7'>Банк:</span> <span className='fw-bold'>{channel.bank}</span>
        </div>
      )}
      {channel.cardholder && (
        <div>
          <span className='text-muted fs-7'>Держатель:</span>{' '}
          <span className='fw-bold'>{channel.cardholder}</span>
        </div>
      )}
    </div>
  )
}
